import React, { useEffect, useRef, useState } from "react";
import { Button, Row, Col, Popover, Modal } from "antd";
import {
  HeartOutlined,
  SoundOutlined,
  MutedOutlined,
} from "@ant-design/icons";
import { FTMS_SAFETY_CONFIG } from "../utils/ftmsSafetyConfig";

const BikeIcon = ({ size = 16, style }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ verticalAlign: "-0.15em", ...style }}
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M7.5 16.5L10.2 9.8H13.5L15 13H10.9"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14.5 7.5H12.8"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle
      cx="6.5"
      cy="16.5"
      r="3.5"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <circle
      cx="17.5"
      cy="16.5"
      r="3.5"
      stroke="currentColor"
      strokeWidth="1.8"
    />
  </svg>
);

const HomeTrainerConnectControls = ({
  isMobile = false,
  isBPMConnected,
  simulate,
  sessionRunnable,
  canErgControl = false,
  onToggleErg,
  pause,
  isMute,
  onConnect,
  onDisconnect,
  onRideWithoutTrainer,
  connectToBPM,
  disconnectFromBPM,
  onToggleMute,
  connectionIssueDiv,
  showTroublePopover = false,
  controlHeight = 64,
  controlHeightMobile = 52,
}) => {
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [hadFtmsIssue, setHadFtmsIssue] = useState(false);
  const [ergDisclaimerOpen, setErgDisclaimerOpen] = useState(false);
  const expandTimerRef = useRef(null);
  const prevTrainerConnectedRef = useRef(false);
  const prevErgAvailableRef = useRef(false);

  const height = isMobile ? controlHeightMobile : controlHeight;
  const trainerConnected = !!sessionRunnable;
  const hrConnected = !!(simulate || isBPMConnected);

  const ergDisclaimerAckKey = "ftms_erg_disclaimer_ack_v1";

  const hasAckedErgDisclaimer = () => {
    try {
      return window.localStorage?.getItem(ergDisclaimerAckKey) === "1";
    } catch {
      return false;
    }
  };

  const setAckedErgDisclaimer = () => {
    try {
      window.localStorage?.setItem(ergDisclaimerAckKey, "1");
    } catch {
      // ignore storage issues
    }
  };

  const openConnectModal = ({ ftmsIssue = false } = {}) => {
    setHadFtmsIssue(!!ftmsIssue);
    setConnectModalOpen(true);
  };

  const connectWithMode = async (mode) => {
    // Close immediately; the browser Bluetooth picker is the "loading UI".
    setConnectModalOpen(false);
    // If we're starting an FTMS attempt, schedule the *same* troubleshooting modal after 10s.
    // This cannot interrupt the browser picker, but it will be visible immediately after the picker closes.
    if (mode === "ftms") {
      try {
        expandTimerRef.current && clearTimeout(expandTimerRef.current);
      } catch {}
      expandTimerRef.current = setTimeout(() => {
        // Don't pop if already connected. `trainerConnected` here would be
        // the value captured when this closure was created (this timer is
        // scheduled once, at click time), which is stale by the time it
        // fires; the ref is kept current on every render instead.
        if (prevTrainerConnectedRef.current) return;
        openConnectModal({ ftmsIssue: true });
      }, 15000);
    }
    try {
      // `onConnect` is expected to accept an optional options object.
      // mode: 'ftms' | 'sensorsOnly'
      await onConnect?.({ mode });
      setHadFtmsIssue(false);
      setConnectModalOpen(false); // if a "10s" modal opened while waiting, close it on success
    } catch (e) {
      // Only open the "ERG not found? Expand search" modal for:
      // - cancelled picker cases
      // - the 10s timer case (handled above)
      if (mode === "ftms") {
        if (
          e?.name === "NotFoundError" ||
          e?.code === "FTMS_CANCELLED" ||
          e?.code === "EXPANDED_CANCELLED"
        ) {
          // The rider may already be riding by the time the picker is
          // cancelled (e.g. they chose "Ride without a trainer" from this
          // same modal while the native picker was still up). Don't pop this
          // modal back over a session that is already running.
          //
          // `trainerConnected` is captured at the render where Connect was
          // clicked and the Connect button only exists while it is false,
          // so inside this catch it is always false and would never guard
          // anything. Read the ref instead, which tracks the latest render.
          if (!prevTrainerConnectedRef.current) {
            openConnectModal({ ftmsIssue: true });
          }
        }
        return;
      } else {
        // Read-only failed/cancelled: keep it closed; user can click Connect again.
      }
    } finally {
      try {
        expandTimerRef.current && clearTimeout(expandTimerRef.current);
      } catch {}
      expandTimerRef.current = null;
    }
  };

  // If we become connected (or simulate), make sure the helper modal is closed.
  useEffect(() => {
    if (!trainerConnected) return;
    try {
      expandTimerRef.current && clearTimeout(expandTimerRef.current);
    } catch {}
    expandTimerRef.current = null;
    setConnectModalOpen(false);
    setHadFtmsIssue(false);
  }, [trainerConnected]);

  // Show ERG disclaimer exactly when FTMS/ERG becomes available after connect.
  useEffect(() => {
    // Keep this ref updated for other flows, but don't use it as the trigger anymore.
    prevTrainerConnectedRef.current = trainerConnected;

    // Trigger when the app is about to control resistance/power:
    // - Real trainer: once ERG control is actually available (avoids race: connected flips before canErgControl)
    // - Simulate: immediately when virtual connection is enabled
    const disclaimerEligible = Boolean(trainerConnected && (simulate || canErgControl));
    const wasEligible = prevErgAvailableRef.current;
    prevErgAvailableRef.current = disclaimerEligible;

    if (wasEligible || !disclaimerEligible) return;

    if (hasAckedErgDisclaimer()) return;
    setErgDisclaimerOpen(true);
  }, [trainerConnected, canErgControl, simulate]);

  const trainerButton = (
    <Button
      id="tour-connect-trainer"
      block
      style={{ height }}
      className={`primary-glass-btn ${trainerConnected ? "redish-btn" : ""}`}
      onClick={trainerConnected ? onDisconnect : () => connectWithMode("ftms")}
      disabled={!pause}
      type="primary"
      title={trainerConnected ? "Disconnect trainer" : "Connect trainer"}
    >
      <BikeIcon style={{ marginRight: 4 }} />
      <span>{trainerConnected ? "Disconnect" : "Connect"}</span>
    </Button>
  );

  return (
    <div className="glass-panel glass-section">
      <Modal
        title="ERG control disclaimer"
        open={ergDisclaimerOpen}
        closable={false}
        maskClosable={false}
        rootClassName="liquid-glass-modal"
        onCancel={() => {}}
        footer={[
          <Button
            key="disconnect"
            danger
            onClick={async () => {
              setErgDisclaimerOpen(false);
              try {
                await onDisconnect?.();
              } catch {}
            }}
          >
            Disconnect trainer
          </Button>,
          <Button
            key="no-erg"
            onClick={() => {
              setAckedErgDisclaimer();
              setErgDisclaimerOpen(false);
              onToggleErg && onToggleErg(false);
            }}
          >
            Continue without ERG
          </Button>,
          <Button
            key="ack"
            type="primary"
            className="primary-glass-btn"
            onClick={() => {
              setAckedErgDisclaimer();
              setErgDisclaimerOpen(false);
            }}
          >
            I acknowledge
          </Button>,
        ]}
      >
        <div style={{ lineHeight: 1.45 }}>
          <p style={{ marginTop: 0 }}>
            This web app will <strong>take control of your trainer’s ERG / resistance</strong> via Bluetooth,
            and adjust it automatically according to your workout plan.
          </p>
          <p>
            Safety measures have been implemented to reduce the risk of unstable control and excessive commands,
            but <strong>you remain fully responsible</strong> for using this feature with your equipment.
          </p>

          <details style={{ marginTop: 10 }}>
            <summary style={{ cursor: "pointer", fontWeight: 600 }}>
              Read more (FTMS + safety measures)
            </summary>
            <div style={{ marginTop: 10, whiteSpace: "pre-wrap" }}>
              {`What is FTMS?
FTMS (Fitness Machine Service) is a Bluetooth Low Energy standard that lets an app control compatible trainers.
This app writes commands to the trainer’s Fitness Machine Control Point (UUID 0x2AD9), for example “Set Target Power”.

Safety measures in this app:
- No ERG commands are sent when the page is hidden, the session is paused, ERG control is unavailable, or FTMS is missing.
- Target power is clamped to ${FTMS_SAFETY_CONFIG.minW}W–${FTMS_SAFETY_CONFIG.maxW}W.
- Rate limit: at most 1 FTMS write every ${FTMS_SAFETY_CONFIG.rateLimitMs}ms.
- Debounce: if the target changes multiple times during the rate-limit window, only the latest target is kept.
- Anti-jitter: tiny target changes (< ${FTMS_SAFETY_CONFIG.antiJitterW}W) are ignored to avoid command spam.
- Resume: when the page becomes visible again, we wait ~${Math.round(
                FTMS_SAFETY_CONFIG.resumeCooldownMs / 100
              ) / 10}s then send only the latest pending target.
- Disconnect: pending timers/writes are cancelled and future writes are blocked until control is re-established.

If anything feels wrong (unexpected resistance), stop pedaling and disconnect the trainer.`}
            </div>
          </details>
        </div>
      </Modal>

      <Modal
        title={hadFtmsIssue ? "ERG not found?" : "Connect trainer"}
        open={connectModalOpen}
        onCancel={() => {
          setConnectModalOpen(false);
        }}
        rootClassName="liquid-glass-modal"
        footer={[
          <Button key="cancel" onClick={() => setConnectModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="rideWithoutTrainer"
            onClick={() => {
              setConnectModalOpen(false);
              onRideWithoutTrainer?.();
            }}
            title="No trainer connected: run the session with the clock, the plan and the charts only"
          >
            Ride without a trainer
          </Button>,
          <Button
            key="expand"
            type="primary"
            className="primary-glass-btn"
            onClick={() => connectWithMode("sensorsOnly")}
          >
            Expand search
          </Button>,
        ]}
      >
        <div style={{ whiteSpace: "pre-wrap" }}>
          {"No ERG/FTMS device selected.\n\n" +
            "Click “Expand search” to show other trainer profiles (power/cadence)\n\n" +
            "Tip: many trainers show multiple Bluetooth names (ex: \"Trainer\" vs \"Trainer Power\").\n\n" +
            "Still nothing? “Ride without a trainer” runs the session with the clock, the plan and the charts only, and power stays empty."}
        </div>
      </Modal>
      <Row gutter={8} align="middle">
        <Col flex="42%">
          {trainerConnected && !pause ? (
            <Popover
              title="Trainer connected"
              content="Pause the session to disconnect the trainer."
            >
              {trainerButton}
            </Popover>
          ) : showTroublePopover && !trainerConnected ? (
            <Popover
              title="Trouble Connecting Trainer to Computer?"
              content={connectionIssueDiv}
            >
              {trainerButton}
            </Popover>
          ) : (
            trainerButton
          )}
        </Col>

        <Col flex="42%">
          <Button
            id="tour-connect-hr"
            block
            style={{ height }}
            className={`primary-glass-btn ${simulate || isBPMConnected ? "redish-btn" : ""}`}
            onClick={isBPMConnected || simulate ? disconnectFromBPM : connectToBPM}
            disabled={!pause}
            type="primary"
            title={hrConnected ? "Disconnect heart-rate monitor" : "Connect heart-rate monitor"}
          >
            <HeartOutlined style={{ marginRight: 4 }} />
            <span>{hrConnected ? "Disconnect" : "Connect"}</span>
          </Button>
        </Col>

        <Col flex="16%">
          <Button
            id="tour-mute"
            block
            style={{ height }}
            className={`primary-glass-btn ${isMute ? "redish-btn" : ""}`}
            onClick={onToggleMute}
            type="primary"
            title={isMute ? "Muted (no beep)" : "Sound on (beep at interval end)"}
            aria-label={isMute ? "Muted (no beep)" : "Sound on (beep at interval end)"}
          >
            {isMute ? <MutedOutlined /> : <SoundOutlined />}
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default HomeTrainerConnectControls;


